import { ReactNode, useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Pencil, Upload, Loader2, Image as ImageIcon, Link2, Images } from "lucide-react";
import { useCmsEdit } from "./CmsEditContext";
import { useLocale } from "@/contexts/LocaleContext";
import { getSupabase } from "@/integrations/supabase/lazy";
import { ROUTE_DEFINITIONS } from "@/lib/route-config";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { RichTextEditor } from "@/components/hwm/RichTextEditor";
import { useCmsAccess } from "@/hooks/useCmsAccess";
import { useSeason } from "@/hooks/useSeason";
import { MediaPicker } from "@/cms/MediaPicker";

/**
 * Re-encode an uploaded image to WebP via canvas, downscaling the longest
 * edge to `maxEdge` (px) while preserving aspect ratio. Returns the original
 * file unchanged if it's already a small WebP or if encoding fails.
 */
async function optimizeToWebp(file: File, maxEdge = 1920, quality = 0.82): Promise<Blob> {
  if (!/^image\//.test(file.type) || file.type === "image/svg+xml" || file.type === "image/gif") {
    return file;
  }
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = typeof OffscreenCanvas !== "undefined"
      ? new OffscreenCanvas(w, h)
      : Object.assign(document.createElement("canvas"), { width: w, height: h });
    const ctx = (canvas as any).getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, w, h);
    const blob: Blob | null = "convertToBlob" in canvas
      ? await (canvas as OffscreenCanvas).convertToBlob({ type: "image/webp", quality })
      : await new Promise((res) =>
          (canvas as HTMLCanvasElement).toBlob((b) => res(b), "image/webp", quality)
        );
    if (!blob) return file;
    return blob.size < file.size ? blob : file;
  } catch {
    return file;
  }
}

type TextProps = {
  id: string;
  type?: "text";
  multiline?: boolean;
  children: ReactNode;
  defaultValue?: string;
  as?: keyof JSX.IntrinsicElements;
  className?: string;
  wrapperClassName?: string;
  scope?: "page" | "global";
  /** Render the stored value as raw HTML (dangerouslySetInnerHTML). */
  html?: boolean;
};

type ImageProps = {
  id: string;
  type: "image";
  src: string;
  alt?: string;
  className?: string;
  wrapperClassName?: string;
  style?: CSSProperties;
  scope?: "page" | "global";
  /** Optional link target — wraps image in <a>. Editable via dialog. */
  href?: string;
  /** Aria label for link wrapper. */
  ariaLabel?: string;
  /** Default open-in-new-tab when no CMS override stored. */
  external?: boolean;
  /** Extra className on the link wrapper. */
  linkClassName?: string;
};

type LinkProps = {
  id: string;
  type: "link";
  label: string;
  href: string;
  className?: string;
  wrapperClassName?: string;
  style?: CSSProperties;
  external?: boolean;
  scope?: "page" | "global";
};

type Props = TextProps | ImageProps | LinkProps;

const ACCENT = "#6C184F";

function DeferredImage({
  src,
  alt,
  className,
  style,
}: {
  src: string;
  alt: string;
  className?: string;
  style?: CSSProperties;
}) {
  const ref = useRef<HTMLImageElement | null>(null);
  const [activeSrc, setActiveSrc] = useState("");

  useEffect(() => {
    setActiveSrc("");
    const el = ref.current;
    if (!el) return;
    if (!("IntersectionObserver" in window)) {
      const timer = globalThis.setTimeout(() => setActiveSrc(src), 1200);
      return () => globalThis.clearTimeout(timer);
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setActiveSrc(src);
        observer.disconnect();
      },
      { rootMargin: "300px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [src]);

  return (
    <img
      ref={ref}
      src={activeSrc || undefined}
      alt={alt}
      className={className}
      style={style}
      width={1600}
      height={1067}
      loading="lazy"
      decoding="async"
    />
  );
}

export function Editable(props: Props) {
  const { isEditor, editMode, get, getEn, getImage, getRawImage, getLink, save, pageSlug } = useCmsEdit();
  const { locale } = useLocale();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const scope: "page" | "global" = props.scope ?? "page";
  const slugForUploads = scope === "global" ? "_global" : pageSlug;
  // EN ist read-only und wird ausschließlich aus dem Übersetzungstool gespeist —
  // DB-Overrides (die nur im DE-Editor entstehen) dürfen die EN-Ansicht nicht überschreiben.
  const isEnglish = locale === "en";
  const interactive = isEditor && editMode && !!slugForUploads && !isEnglish;



  // ---------- IMAGE ----------
  if (props.type === "image") {
    const {
      id, src: fallbackSrc, alt: fallbackAlt, className, wrapperClassName, style,
      href: fallbackHref, ariaLabel, external, linkClassName,
    } = props;
    const img = getImage(id, fallbackSrc, scope);
    const raw = getRawImage(id, scope) as any;
    const finalAlt = img.alt ?? fallbackAlt ?? "";
    const finalHref = img.href ?? fallbackHref ?? "";
    const isExternalHref = /^https?:\/\//i.test(finalHref);
    const openNewTab = img.newTab ?? (external || isExternalHref);
    const imgEl = interactive
      ? <img src={img.src} alt={finalAlt} className={className} style={style} width={1600} height={1067} loading="lazy" decoding="async" />
      : <DeferredImage src={img.src} alt={finalAlt} className={className} style={style} />;

    const linkable = !!finalHref;
    const wrapImg = (child: ReactNode) =>
      linkable ? (
        openNewTab || isExternalHref ? (
          <a href={finalHref} target={openNewTab ? "_blank" : undefined} rel={openNewTab ? "noopener noreferrer" : undefined} aria-label={ariaLabel ?? finalAlt} className={linkClassName}>
            {child}
          </a>
        ) : (
          <RouterLink to={finalHref} aria-label={ariaLabel ?? finalAlt} className={linkClassName}>{child}</RouterLink>
        )
      ) : child;

    if (!interactive) {
      const content = wrapImg(imgEl);
      return wrapperClassName ? <span className={wrapperClassName}>{content}</span> : <>{content}</>;
    }

    return (
      <>
        <span className={wrapperClassName ?? "relative inline-block"}>
          {imgEl}
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{ outline: `3px dashed ${ACCENT}`, outlineOffset: "-3px", background: "rgba(108,24,79,0.08)" }}
          />
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
            className="absolute bottom-4 right-4 z-40 inline-flex items-center gap-1.5 rounded-full text-white text-sm font-semibold px-4 py-2 shadow-2xl transition-transform hover:scale-105 pointer-events-auto"
            style={{ background: ACCENT }}
          >
            <ImageIcon className="h-4 w-4" /> Bild ändern
          </button>
        </span>
        <ImageDialog
          open={open} onOpenChange={setOpen}
          currentSrc={(raw?.value as string) || fallbackSrc}
          currentAlt={(raw?.alt as string) ?? fallbackAlt ?? ""}
          currentSeasonal={!!raw?.seasonal}
          currentSrcWinter={(raw?.valueWinter as string) ?? ""}
          currentAltWinter={(raw?.altWinter as string) ?? ""}
          currentHref={finalHref} currentNewTab={openNewTab}
          showLink={linkable || !!fallbackHref || true}
          pageSlug={slugForUploads!} sectionKey={id}
          saving={saving} setSaving={setSaving}
          onSaved={(url, alt, href, newTab, seasonal, urlWinter, altWinter) =>
            save(id, "image", url, alt, { href, newTab, seasonal, valueWinter: urlWinter, altWinter }, scope)
          }
        />
      </>
    );
  }

  // ---------- LINK / BUTTON ----------
  if (props.type === "link") {
    const { id, label: fallbackLabel, href: fallbackHref, className, wrapperClassName, style, external } = props;
    const rawLink = getLink(id, fallbackLabel, fallbackHref, scope);
    // EN: Link-Adresse kommt aus dem Übersetzungstool (fallbackHref = t(urlKey)),
    // im DE-Editor gespeicherte Adressen dürfen die englische nicht überschreiben.
    const link = isEnglish && fallbackHref ? { ...rawLink, href: fallbackHref } : rawLink;
    const isExternalHref = /^https?:\/\//i.test(link.href);
    const openNewTab = link.newTab ?? (external || isExternalHref);
    // EN: bevorzuge auto-übersetztes Label aus cms_sections; sonst statisches Fallback (kommt aus EditableTLink via t()).
    const displayLabel = isEnglish ? (link.label_en ?? fallbackLabel) : link.label;

    const anchor = openNewTab || isExternalHref ? (
      <a href={link.href} target={openNewTab ? "_blank" : undefined} rel={openNewTab ? "noopener noreferrer" : undefined} className={className} style={style}>
        {displayLabel}
      </a>
    ) : (
      <RouterLink to={link.href} className={className} style={style}>
        {displayLabel}
      </RouterLink>
    );

    if (!interactive) return anchor;

    return (
      <>
        <span
          className={`relative inline-block cursor-pointer group/edit ${wrapperClassName ?? ""}`}
          style={{ outline: `2px dashed ${ACCENT}`, outlineOffset: "4px" }}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
          title="Button bearbeiten"
        >
          {anchor}
          <span
            className="absolute -top-3 -right-3 z-20 inline-flex items-center gap-1 overflow-hidden rounded-full text-white text-[10px] font-bold leading-none font-sans shadow-lg transition-all duration-200 h-6 w-6 group-hover/edit:w-[92px] group-hover/edit:scale-110"
            style={{ background: ACCENT }}
          >
            <Pencil className="h-3 w-3 shrink-0 ml-[6px]" />
            <span className="opacity-0 group-hover/edit:opacity-100 transition-opacity whitespace-nowrap pr-2 normal-case tracking-normal">Bearbeiten</span>
          </span>
        </span>
        <LinkDialog
          open={open} onOpenChange={setOpen}
          initialLabel={link.label} initialHref={link.href} initialNewTab={openNewTab}
          saving={saving} setSaving={setSaving}
          onSave={async (lbl, hrf, nt) => { await save(id, "link", lbl, hrf, { newTab: nt }, scope); }}
        />
      </>
    );
  }

  // ---------- TEXT ----------
  const { id, multiline, children, defaultValue, as = "span", className, wrapperClassName, html } = props;
  const initialValue = defaultValue ?? (typeof children === "string" ? children : "");
  // EN: auto-übersetzten Wert (value_en) bevorzugen; sonst statisches Fallback (defaultValue/children).
  // DE: ganz normal aus cms_sections.value.
  const value = isEnglish ? (getEn(id, scope) ?? "") : get(id, "", scope);
  const Tag: any = as;
  const renderValue = (v: string) =>
    html ? <Tag className={className} dangerouslySetInnerHTML={{ __html: v }} /> : <Tag className={className}>{v}</Tag>;

  if (!interactive) {
    return value ? renderValue(value) : <Tag className={className}>{children}</Tag>;
  }

  return (
    <>
      <span
        className={`relative inline-block align-baseline cursor-pointer group/edit ${wrapperClassName ?? ""}`}
        style={{ outline: `2px dashed ${ACCENT}`, outlineOffset: "4px", background: "rgba(108,24,79,0.06)" }}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        title="Klicken zum Bearbeiten"
      >
        {value ? renderValue(value) : <Tag className={className}>{children}</Tag>}
        <span
          className="absolute -top-3 -right-3 z-20 inline-flex items-center gap-1 overflow-hidden rounded-full text-white text-[10px] font-bold leading-none font-sans shadow-lg transition-all duration-200 h-6 w-6 group-hover/edit:w-[92px] group-hover/edit:scale-110"
          style={{ background: ACCENT }}
        >
          <Pencil className="h-3 w-3 shrink-0 ml-[6px]" />
          <span className="opacity-0 group-hover/edit:opacity-100 transition-opacity whitespace-nowrap pr-2 normal-case tracking-normal">Bearbeiten</span>
        </span>
      </span>
      <TextDialog
        open={open} onOpenChange={setOpen}
        initial={value || initialValue}
        multiline={!!multiline}
        html={!!html}
        saving={saving} setSaving={setSaving}
        onSave={async (v) => { await save(id, "text", v, undefined, undefined, scope); }}
      />
    </>
  );
}

function LinkDialog({
  open, onOpenChange, initialLabel, initialHref, initialNewTab, saving, setSaving, onSave,
}: {
  open: boolean; onOpenChange: (v: boolean) => void;
  initialLabel: string; initialHref: string; initialNewTab: boolean;
  saving: boolean; setSaving: (v: boolean) => void;
  onSave: (label: string, href: string, newTab: boolean) => Promise<void>;
}) {
  const [lbl, setLbl] = useState(initialLabel);
  const [hrf, setHrf] = useState(initialHref);
  const [newTab, setNewTab] = useState(initialNewTab);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (o) { setLbl(initialLabel); setHrf(initialHref); setNewTab(initialNewTab); } onOpenChange(o); }}>
      <DialogContent>
        <DialogHeader><DialogTitle>Button bearbeiten</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label className="text-xs">Beschriftung</Label>
            <Input value={lbl} onChange={(e) => setLbl(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Interne Seite verlinken</Label>
            <Select onValueChange={(v) => setHrf(v)}>
              <SelectTrigger>
                <SelectValue placeholder="Seite auswählen…" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Seiten</SelectLabel>
                  {ROUTE_DEFINITIONS.filter((r) => r.kind !== "post").map((r) => (
                    <SelectItem key={r.path} value={r.path}>
                      {r.title}
                    </SelectItem>
                  ))}
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>Suiten</SelectLabel>
                  {ROUTE_DEFINITIONS.filter((r) => r.kind === "post").map((r) => (
                    <SelectItem key={r.path} value={r.path}>
                      {r.title}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Oder Link-Ziel manuell eingeben (intern z. B. /buchen oder vollständige URL)</Label>
            <Input value={hrf} onChange={(e) => setHrf(e.target.value)} placeholder="/buchen oder https://…" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={newTab}
              onChange={(e) => setNewTab(e.target.checked)}
              className="h-4 w-4 rounded border-input accent-primary"
            />
            In neuem Tab öffnen
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              try { await onSave(lbl, hrf, newTab); toast.success("Gespeichert"); onOpenChange(false); }
              catch (e: any) { toast.error(e.message ?? "Fehler"); }
              finally { setSaving(false); }
            }}
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export function TextDialog({
  open, onOpenChange, initial, multiline, html, saving, setSaving, onSave,
}: {
  open: boolean; onOpenChange: (v: boolean) => void;
  initial: string; multiline: boolean; html?: boolean;
  saving: boolean; setSaving: (v: boolean) => void;
  onSave: (v: string) => Promise<void>;
}) {
  const [val, setVal] = useState(initial);
  const useRich = html || multiline;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (o) setVal(initial); onOpenChange(o); }}>
      <DialogContent className={useRich ? "sm:max-w-2xl" : undefined}>
        <DialogHeader><DialogTitle>Inhalt bearbeiten</DialogTitle></DialogHeader>
        {useRich ? (
          <RichTextEditor value={val} onChange={setVal} minRows={6} />
        ) : (
          <Input value={val} onChange={(e) => setVal(e.target.value)} />
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              try { await onSave(val); toast.success("Gespeichert"); onOpenChange(false); }
              catch (e: any) { toast.error(e.message ?? "Fehler"); }
              finally { setSaving(false); }
            }}
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImageDialog({
  open, onOpenChange, currentSrc, currentAlt, currentHref, currentNewTab, showLink,
  currentSeasonal, currentSrcWinter, currentAltWinter,
  pageSlug, sectionKey, saving, setSaving, onSaved,
}: {
  open: boolean; onOpenChange: (v: boolean) => void;
  currentSrc: string; currentAlt: string;
  currentHref?: string; currentNewTab?: boolean; showLink?: boolean;
  currentSeasonal?: boolean; currentSrcWinter?: string; currentAltWinter?: string;
  pageSlug: string; sectionKey: string;
  saving: boolean; setSaving: (v: boolean) => void;
  onSaved: (url: string, alt: string, href: string, newTab: boolean, seasonal: boolean, urlWinter: string, altWinter: string) => Promise<void>;
}) {
  const { access } = useCmsAccess();
  const season = useSeason();
  const canSeason = access.hasTab("season");
  const [url, setUrl] = useState(currentSrc);
  const [alt, setAlt] = useState(currentAlt);
  const [href, setHref] = useState(currentHref ?? "");
  const [newTab, setNewTab] = useState(currentNewTab ?? true);
  const [seasonal, setSeasonal] = useState(!!currentSeasonal);
  const [urlWinter, setUrlWinter] = useState(currentSrcWinter ?? "");
  const [altWinter, setAltWinter] = useState(currentAltWinter ?? "");
  const [uploading, setUploading] = useState(false);
  const [uploadingWinter, setUploadingWinter] = useState(false);
  const [pickerOpen, setPickerOpen] = useState<null | "summer" | "winter">(null);
  // Wenn der User keine Saison-Berechtigung hat und das Bild saisonal ist,
  // zeigen/bearbeiten wir nur das Bild der aktuell aktiven Saison.
  const editWinter = !canSeason && seasonal && season === "winter";
  const displayUrl = editWinter ? urlWinter : url;
  const displayAlt = editWinter ? altWinter : alt;
  const setDisplayUrl = editWinter ? setUrlWinter : setUrl;
  const setDisplayAlt = editWinter ? setAltWinter : setAlt;

  const filenameToAlt = (name: string) => {
    const base = name.replace(/\.[^.]+$/, "");
    const cleaned = base
      .replace(/[-_]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!cleaned) return "";
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  };

  const uploadTo = async (file: File, target: "summer" | "winter") => {
    const setBusy = target === "summer" ? setUploading : setUploadingWinter;
    setBusy(true);
    try {
      const optimized = await optimizeToWebp(file, 1920, 0.82);
      const { sanitizeName } = await import("@/cms/lib/mediaLibrary");
      const baseName = sanitizeName(file.name.replace(/\.[^.]+$/, ""));
      const suffix = target === "winter" ? "-winter" : "";
      const path = `${pageSlug}/${sectionKey}${suffix}-${Date.now()}-${baseName}.webp`;

      const { error } = await (await getSupabase()).storage
        .from("cms-media")
        .upload(path, optimized, { upsert: true, contentType: "image/webp", cacheControl: "31536000" });
      if (error) throw error;
      const { data, error: signErr } = await (await getSupabase()).storage
        .from("cms-media")
        .createSignedUrl(path, 60 * 60 * 24 * 365 * 10);
      if (signErr || !data) throw signErr ?? new Error("URL fehlgeschlagen");
      const autoAlt = filenameToAlt(file.name);
      if (target === "summer") {
        setUrl(data.signedUrl);
        if (autoAlt && !alt.trim()) setAlt(autoAlt);
      } else {
        setUrlWinter(data.signedUrl);
        if (autoAlt && !altWinter.trim()) setAltWinter(autoAlt);
      }
      const savedKb = Math.max(0, Math.round((file.size - optimized.size) / 1024));
      toast.success(`Bild optimiert & hochgeladen (−${savedKb} KB, WebP)`);

      // In Mediathek registrieren + Auto-Metadaten triggern
      try {
        const { data: { user } } = await (await getSupabase()).auth.getUser();
        const baseNameClean = file.name.replace(/\.[^.]+$/, "");
        const { data: row } = await ((await getSupabase()) as any).from("cms_media").insert({
          storage_path: path,
          url: data.signedUrl,
          filename: file.name,
          mime_type: "image/webp",
          size_bytes: optimized.size,
          folder: pageSlug,
          tags: [pageSlug, sectionKey].filter(Boolean),
          title: baseNameClean,
          alt_text: autoAlt || "",
          uploaded_by: user?.id ?? null,
        }).select("*").single();
        if (row) {
          const { generateMetadataFor } = await import("@/cms/lib/mediaLibrary");
          void generateMetadataFor(row as any).catch(() => {});
        }
      } catch { /* ignore – Bild ist trotzdem hochgeladen */ }
    } catch (e: any) {
      toast.error(e.message ?? "Upload-Fehler");
    } finally { setBusy(false); }
  };


  return (
    <Dialog open={open} onOpenChange={(o) => {
      if (o) {
        setUrl(currentSrc); setAlt(currentAlt); setHref(currentHref ?? ""); setNewTab(currentNewTab ?? true);
        setSeasonal(!!currentSeasonal); setUrlWinter(currentSrcWinter ?? ""); setAltWinter(currentAltWinter ?? "");
      }
      onOpenChange(o);
    }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Bild bearbeiten</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {/* SUMMER / Standard (oder aktive Saison, wenn User keine Saison-Berechtigung hat) */}
          <div className="space-y-2">
            {seasonal && canSeason && <Label className="text-xs font-semibold uppercase tracking-wide text-[#6C184F]">Sommer</Label>}
            <div className="w-full aspect-[16/9] bg-muted rounded overflow-hidden">
              {displayUrl && <img src={displayUrl} alt={displayAlt} className="w-full h-full object-cover" width={1600} height={1067} loading="lazy" decoding="async" />}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer text-primary hover:underline">
                {(editWinter ? uploadingWinter : uploading) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                <span>{(editWinter ? uploadingWinter : uploading) ? "Lädt hoch…" : (seasonal && canSeason ? "Sommer-Bild hochladen" : "Bild hochladen")}</span>
                <input type="file" accept="image/*" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadTo(f, editWinter ? "winter" : "summer"); }} />
              </label>
              <Button type="button" variant="outline" size="sm" onClick={() => setPickerOpen(editWinter ? "winter" : "summer")}>
                <Images className="h-4 w-4 mr-2" /> Aus Mediathek
              </Button>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Alt-Text</Label>
              <Input value={displayAlt} onChange={(e) => setDisplayAlt(e.target.value)} />
            </div>
          </div>

          {/* Seasonal checkbox – nur sichtbar, wenn User Saison-Berechtigung hat */}
          {canSeason && (
            <SeasonalSection
              seasonal={seasonal}
              setSeasonal={setSeasonal}
              urlWinter={urlWinter}
              altWinter={altWinter}
              setAltWinter={setAltWinter}
              uploadingWinter={uploadingWinter}
              onUpload={(f) => uploadTo(f, "winter")}
            />
          )}


          {showLink !== false && (
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                <Link2 className="h-3.5 w-3.5" /> Verlinkung (optional)
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Interne Seite verlinken</Label>
                <Select onValueChange={(v) => setHref(v)}>
                  <SelectTrigger><SelectValue placeholder="Seite auswählen…" /></SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Seiten</SelectLabel>
                      {ROUTE_DEFINITIONS.filter((r) => r.kind !== "post").map((r) => (
                        <SelectItem key={r.path} value={r.path}>{r.title}</SelectItem>
                      ))}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel>Suiten</SelectLabel>
                      {ROUTE_DEFINITIONS.filter((r) => r.kind === "post").map((r) => (
                        <SelectItem key={r.path} value={r.path}>{r.title}</SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Oder Link-Ziel manuell (z. B. /buchen oder https://…)</Label>
                <Input value={href} onChange={(e) => setHref(e.target.value)} placeholder="https://… oder /pfad — leer lassen für keine Verlinkung" />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={newTab}
                  onChange={(e) => setNewTab(e.target.checked)}
                  className="h-4 w-4 rounded border-input accent-primary"
                />
                In neuem Tab öffnen
              </label>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button
            disabled={saving || uploading || uploadingWinter}
            onClick={async () => {
              setSaving(true);
              try { await onSaved(url, alt, href, newTab, seasonal, urlWinter, altWinter); toast.success("Gespeichert"); onOpenChange(false); }
              catch (e: any) { toast.error(e.message ?? "Fehler"); }
              finally { setSaving(false); }
            }}
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
      <MediaPicker
        open={pickerOpen !== null}
        onOpenChange={(o) => { if (!o) setPickerOpen(null); }}
        defaultFolder={pageSlug}
        onSelect={(it) => {
          if (pickerOpen === "winter") {
            setUrlWinter(it.url);
            if (!altWinter.trim()) setAltWinter(it.alt_text || it.title || "");
          } else {
            setUrl(it.url);
            if (!alt.trim()) setAlt(it.alt_text || it.title || "");
          }
        }}
      />
    </Dialog>
  );
}

// ============================================================================
// EditableVideo – inline-editable <video> with CMS-managed src + poster.
// Note: browsers can't reliably transcode video client-side without bundling
// ffmpeg.wasm (~30MB). The file is uploaded as-is; we enforce a soft cap and
// recommend pre-compressed MP4/WebM (H.264/VP9, ~1080p).
// ============================================================================
const VIDEO_MAX_BYTES = 100 * 1024 * 1024; // 100 MB

type EditableVideoProps = {
  id: string;
  src: string;
  /** Kleinere Datei für Mobilgeräte (spart Ladezeit / Datenvolumen). */
  srcMobile?: string;
  poster?: string;
  className?: string;
  wrapperClassName?: string;
  scope?: "page" | "global";
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  playsInline?: boolean;
  ariaHidden?: boolean;
};

export function EditableVideo({
  id, src: fallbackSrc, srcMobile, poster: fallbackPoster, className, wrapperClassName,
  scope = "page", autoPlay = true, loop = true, muted = true, playsInline = true,
  ariaHidden = true,
}: EditableVideoProps) {
  const { isEditor, editMode, get, save, pageSlug, season } = useCmsEdit();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const slugForUploads = scope === "global" ? "_global" : pageSlug;
  const interactive = isEditor && editMode && !!slugForUploads;

  const customSrc = get(`${id}.src`, "", scope);
  const customPoster = get(`${id}.poster`, "", scope);
  const customSrcWinter = get(`${id}.srcWinter`, "", scope);
  const customPosterWinter = get(`${id}.posterWinter`, "", scope);
  const seasonalFlag = get(`${id}.seasonal`, "", scope) === "1";

  const seasonalSrc = seasonalFlag && season === "winter" && customSrcWinter
    ? customSrcWinter
    : (customSrc || fallbackSrc);
  // Mobil: leichtere Datei verwenden (nur wenn kein CMS-Video hinterlegt ist)
  const isSmallScreen = typeof window !== "undefined"
    && window.matchMedia("(max-width: 767px)").matches;
  const finalSrc = isSmallScreen && srcMobile && !customSrc && !customSrcWinter
    ? srcMobile
    : seasonalSrc;
  const finalPoster = seasonalFlag && season === "winter" && customPosterWinter
    ? customPosterWinter
    : (customPoster || fallbackPoster);

  // Das Video wird erst nach dem ersten Rendern (Page-Load / Idle) geladen,
  // damit Poster-Bild = LCP bleibt und der Mobile-PageSpeed nicht leidet.
  const [activeSrc, setActiveSrc] = useState("");
  useEffect(() => {
    if (interactive) { setActiveSrc(finalSrc); return; }
    let timer: number | undefined;
    let cancelled = false;
    const start = () => {
      if (cancelled) return;
      cancelled = true;
      cleanup();
      setActiveSrc(finalSrc);
    };
    const evts: (keyof WindowEventMap)[] = ["pointerdown", "touchstart", "scroll", "keydown"];
    const cleanup = () => {
      evts.forEach((e) => window.removeEventListener(e, start));
      window.removeEventListener("load", schedule);
      if (timer) window.clearTimeout(timer);
    };
    // Video erst nach dem Load + Ruhephase (oder bei erster Interaktion) laden:
    // so bleibt der Main-Thread direkt nach dem Laden frei und Scrollen ruckelt nicht.
    const schedule = () => { timer = window.setTimeout(start, 1500); };
    if (document.readyState === "complete") schedule();
    else window.addEventListener("load", schedule, { once: true });
    evts.forEach((e) => window.addEventListener(e, start, { passive: true, once: true }));
    return () => { cancelled = true; cleanup(); };
  }, [finalSrc, interactive]);


  const videoEl = (
    <video
      key={finalSrc}
      className={className}
      src={activeSrc || undefined}
      poster={finalPoster}
      autoPlay={autoPlay}
      muted={muted}
      loop={loop}
      playsInline={playsInline}
      aria-hidden={ariaHidden}
      disablePictureInPicture
      disableRemotePlayback
      controls={false}
      preload="none"
      onLoadedData={(e) => { if (autoPlay) e.currentTarget.play().catch(() => {}); }}
      tabIndex={-1}
      style={{ pointerEvents: "none", touchAction: "pan-y" }}
    />
  );

  if (!interactive) return videoEl;

  return (
    <>
      <span className={wrapperClassName ?? "relative block w-full h-full"}>
        {videoEl}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 z-30"
          style={{ outline: `3px dashed ${ACCENT}`, outlineOffset: "-3px", background: "rgba(108,24,79,0.08)" }}
        />
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
          className="absolute bottom-4 right-4 z-40 inline-flex items-center gap-1.5 rounded-full text-white text-sm font-semibold px-4 py-2 shadow-2xl transition-transform hover:scale-105 pointer-events-auto"
          style={{ background: ACCENT }}
        >
          <ImageIcon className="h-4 w-4" /> Video ändern
        </button>
      </span>
      <VideoDialog
        open={open} onOpenChange={setOpen}
        currentSrc={customSrc || fallbackSrc || ""} currentPoster={customPoster || fallbackPoster || ""}
        currentSeasonal={seasonalFlag}
        currentSrcWinter={customSrcWinter} currentPosterWinter={customPosterWinter}
        pageSlug={slugForUploads!} sectionKey={id}
        saving={saving} setSaving={setSaving}
        onSaved={async (videoUrl, posterUrl, seasonalNew, videoUrlWinter, posterUrlWinter) => {
          await save(`${id}.src`, "text", videoUrl, undefined, undefined, scope);
          await save(`${id}.poster`, "text", posterUrl, undefined, undefined, scope);
          await save(`${id}.seasonal`, "text", seasonalNew ? "1" : "", undefined, undefined, scope);
          await save(`${id}.srcWinter`, "text", seasonalNew ? videoUrlWinter : "", undefined, undefined, scope);
          await save(`${id}.posterWinter`, "text", seasonalNew ? posterUrlWinter : "", undefined, undefined, scope);
        }}
      />
    </>
  );
}

function VideoDialog({
  open, onOpenChange, currentSrc, currentPoster, currentSeasonal, currentSrcWinter, currentPosterWinter,
  pageSlug, sectionKey, saving, setSaving, onSaved,
}: {
  open: boolean; onOpenChange: (v: boolean) => void;
  currentSrc: string; currentPoster: string;
  currentSeasonal?: boolean; currentSrcWinter?: string; currentPosterWinter?: string;
  pageSlug: string; sectionKey: string;
  saving: boolean; setSaving: (v: boolean) => void;
  onSaved: (videoUrl: string, posterUrl: string, seasonal: boolean, videoUrlWinter: string, posterUrlWinter: string) => Promise<void>;
}) {
  const { access } = useCmsAccess();
  const [videoUrl, setVideoUrl] = useState(currentSrc);
  const [posterUrl, setPosterUrl] = useState(currentPoster);
  const [seasonal, setSeasonal] = useState(!!currentSeasonal);
  const [videoUrlWinter, setVideoUrlWinter] = useState(currentSrcWinter ?? "");
  const [posterUrlWinter, setPosterUrlWinter] = useState(currentPosterWinter ?? "");
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingPoster, setUploadingPoster] = useState(false);
  const [uploadingVideoW, setUploadingVideoW] = useState(false);
  const [uploadingPosterW, setUploadingPosterW] = useState(false);

  const uploadVideo = async (file: File, target: "summer" | "winter") => {
    if (file.size > VIDEO_MAX_BYTES) {
      toast.error(`Video zu groß (${(file.size / 1024 / 1024).toFixed(1)} MB). Max. 100 MB. Bitte vorher komprimieren.`);
      return;
    }
    const setBusy = target === "summer" ? setUploadingVideo : setUploadingVideoW;
    setBusy(true);
    try {
      const ext = (file.name.match(/\.[a-z0-9]+$/i)?.[0] ?? ".mp4").toLowerCase();
      const suffix = target === "winter" ? "-winter" : "";
      const path = `${pageSlug}/${sectionKey}${suffix}-${Date.now()}${ext}`;
      const { error } = await (await getSupabase()).storage
        .from("cms-media")
        .upload(path, file, { upsert: true, contentType: file.type || "video/mp4", cacheControl: "31536000" });
      if (error) throw error;
      const { data, error: signErr } = await (await getSupabase()).storage
        .from("cms-media")
        .createSignedUrl(path, 60 * 60 * 24 * 365 * 10);
      if (signErr || !data) throw signErr ?? new Error("URL fehlgeschlagen");
      if (target === "summer") setVideoUrl(data.signedUrl); else setVideoUrlWinter(data.signedUrl);
      toast.success(`Video hochgeladen (${(file.size / 1024 / 1024).toFixed(1)} MB)`);
    } catch (e: any) {
      toast.error(e.message ?? "Upload-Fehler");
    } finally { setBusy(false); }
  };

  const uploadPoster = async (file: File, target: "summer" | "winter") => {
    const setBusy = target === "summer" ? setUploadingPoster : setUploadingPosterW;
    setBusy(true);
    try {
      const optimized = await optimizeToWebp(file, 1920, 0.82);
      const { sanitizeName } = await import("@/cms/lib/mediaLibrary");
      const baseName = sanitizeName(file.name.replace(/\.[^.]+$/, ""));
      const suffix = target === "winter" ? "-winter" : "";
      const path = `${pageSlug}/${sectionKey}-poster${suffix}-${Date.now()}-${baseName}.webp`;

      const { error } = await (await getSupabase()).storage
        .from("cms-media")
        .upload(path, optimized, { upsert: true, contentType: "image/webp", cacheControl: "31536000" });
      if (error) throw error;
      const { data, error: signErr } = await (await getSupabase()).storage
        .from("cms-media")
        .createSignedUrl(path, 60 * 60 * 24 * 365 * 10);
      if (signErr || !data) throw signErr ?? new Error("URL fehlgeschlagen");
      if (target === "summer") setPosterUrl(data.signedUrl); else setPosterUrlWinter(data.signedUrl);
      toast.success("Poster optimiert & hochgeladen (WebP)");
    } catch (e: any) {
      toast.error(e.message ?? "Upload-Fehler");
    } finally { setBusy(false); }
  };

  const renderMediaBlock = (target: "summer" | "winter") => {
    const v = target === "summer" ? videoUrl : videoUrlWinter;
    const p = target === "summer" ? posterUrl : posterUrlWinter;
    const ulV = target === "summer" ? uploadingVideo : uploadingVideoW;
    const ulP = target === "summer" ? uploadingPoster : uploadingPosterW;
    const label = target === "summer" ? "Sommer" : "Winter";
    const color = target === "summer" ? "text-[#6C184F]" : "text-blue-700";
    return (
      <div className="space-y-2">
        {seasonal && <Label className={`text-xs font-semibold uppercase tracking-wide ${color}`}>{label}</Label>}
        <div className="w-full aspect-video bg-muted rounded overflow-hidden">
          {v
            ? <video key={v} src={v} poster={p} className="w-full h-full object-cover" muted loop playsInline autoPlay />
            : <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">Noch kein Video</div>}
        </div>
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer text-primary hover:underline">
          {ulV ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          <span>{ulV ? "Lädt hoch…" : `${seasonal ? label + "-" : ""}Video hochladen (MP4/WebM)`}</span>
          <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadVideo(f, target); }} />
        </label>
        <div className="pt-1">
          <Label className="text-xs">Poster / Vorschaubild</Label>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer text-primary hover:underline">
            {ulP ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            <span>{ulP ? "Lädt hoch…" : "Poster hochladen (WebP)"}</span>
            <input type="file" accept="image/*" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPoster(f, target); }} />
          </label>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => {
      if (o) {
        setVideoUrl(currentSrc); setPosterUrl(currentPoster);
        setSeasonal(!!currentSeasonal);
        setVideoUrlWinter(currentSrcWinter ?? ""); setPosterUrlWinter(currentPosterWinter ?? "");
      }
      onOpenChange(o);
    }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Video bearbeiten</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {renderMediaBlock("summer")}

          {access.hasTab("season") && (
            <>
              <div className="border-t pt-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={seasonal}
                    onChange={(e) => setSeasonal(e.target.checked)}
                    className="h-4 w-4 rounded border-input accent-primary"
                  />
                  <span className="font-medium">Verschiedene Videos für Jahreszeiten verwenden</span>
                </label>
                <p className="text-[11px] text-muted-foreground mt-1 ml-6">
                  Das Sommer-Video wird standardmäßig angezeigt. Im Winter-Modus wird unten gewähltes Video eingeblendet.
                </p>
              </div>

              {seasonal && <div className="border-t pt-4">{renderMediaBlock("winter")}</div>}
            </>
          )}

          <p className="text-[11px] text-muted-foreground leading-snug border-t pt-3">
            Tipp: Videos vorab auf 1080p H.264/MP4 oder VP9/WebM komprimieren (z. B. mit HandBrake). Max. 100 MB.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button
            disabled={saving || uploadingVideo || uploadingPoster || uploadingVideoW || uploadingPosterW}
            onClick={async () => {
              setSaving(true);
              try { await onSaved(videoUrl, posterUrl, seasonal, videoUrlWinter, posterUrlWinter); toast.success("Gespeichert"); onOpenChange(false); }
              catch (e: any) { toast.error(e.message ?? "Fehler"); }
              finally { setSaving(false); }
            }}
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SeasonalSection({
  seasonal, setSeasonal, urlWinter, altWinter, setAltWinter, uploadingWinter, onUpload,
}: {
  seasonal: boolean;
  setSeasonal: (v: boolean) => void;
  urlWinter: string;
  altWinter: string;
  setAltWinter: (v: string) => void;
  uploadingWinter: boolean;
  onUpload: (f: File) => void;
}) {
  const { access } = useCmsAccess();
  // Saison-Feature komplett ausblenden, wenn User keine Saison-Berechtigung hat.
  if (!access.hasTab("season")) return null;

  return (
    <>
      <div className="border-t pt-3">
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input
            type="checkbox"
            checked={seasonal}
            onChange={(e) => setSeasonal(e.target.checked)}
            disabled={!access.hasTab("season")}
            className="h-4 w-4 rounded border-input accent-primary disabled:opacity-50"
          />
          <span className="font-medium">Verschiedene Bilder für Jahreszeiten verwenden</span>
        </label>
        <p className="text-[11px] text-muted-foreground mt-1 ml-6">
          {access.hasTab("season")
            ? "Das Sommer-Bild wird standardmäßig angezeigt. Im Winter-Modus wird das untere Bild eingeblendet."
            : 'Dieses Bild ist saisonal konfiguriert. Für Änderungen brauchst du die Berechtigung „Saison".'}
        </p>
      </div>

      {seasonal && (
        <div className="space-y-2 border-t pt-4">
          <Label className="text-xs font-semibold uppercase tracking-wide text-blue-700">Winter</Label>
          <div className="w-full aspect-[16/9] bg-muted rounded overflow-hidden">
            {urlWinter
              ? <img src={urlWinter} alt={altWinter} className="w-full h-full object-cover" width={1600} height={1067} loading="lazy" decoding="async" />
              : <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">Noch kein Winter-Bild</div>}
          </div>
          {access.hasTab("season") && (
            <>
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer text-primary hover:underline">
                {uploadingWinter ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                <span>{uploadingWinter ? "Lädt hoch…" : "Winter-Bild hochladen"}</span>
                <input type="file" accept="image/*" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); }} />
              </label>
              <div className="space-y-1">
                <Label className="text-xs">Alt-Text (Winter)</Label>
                <Input value={altWinter} onChange={(e) => setAltWinter(e.target.value)} placeholder="Wenn leer, wird Sommer-Alt-Text verwendet" />
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}


